import { useState, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MediaItem,
  MediaTag,
  createMediaTag,
  deleteMediaTag,
  CreateTagData,
} from "../../api/media";
import { PersonSummary } from "../../api/trees";
import { useMediaUrl } from "../../hooks/useMediaUrl";
import PersonSelector from "../Person/PersonSelector";

interface FaceTaggerProps {
  media: MediaItem;
  persons: PersonSummary[];
  onClose: () => void;
}

interface DrawingRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

export default function FaceTagger({ media, persons, onClose }: FaceTaggerProps) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Get authenticated media URL
  const { url: mediaUrl, isLoading: mediaLoading } = useMediaUrl(media.id);

  // Local state for tags (so we can update immediately after mutation)
  const [localTags, setLocalTags] = useState<MediaTag[]>(media.tags || []);

  // State
  const [imageLoaded, setImageLoaded] = useState(false);
  const [_imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [currentRegion, setCurrentRegion] = useState<DrawingRegion | null>(null);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [showPersonSelect, setShowPersonSelect] = useState(false);

  // Create tag mutation - supports tree, connected persons, and name-only tags
  const createTagMutation = useMutation({
    mutationFn: async (data: { 
      personType: "tree" | "connected" | "name_only";
      personId: number; 
      region: DrawingRegion; 
      personName: string 
    }) => {
      const token = await getAccessTokenSilently();
      const tagData: CreateTagData = {
        region_x: data.region.x,
        region_y: data.region.y,
        region_width: data.region.width,
        region_height: data.region.height,
      };
      if (data.personType === "tree") {
        tagData.person_id = data.personId;
      } else if (data.personType === "connected") {
        tagData.connected_person_id = data.personId;
      } else {
        // name_only - just use the tag_name
        tagData.tag_name = data.personName;
      }
      return createMediaTag(media.id, tagData, token);
    },
    onSuccess: (newTag, variables) => {
      // Update local tags immediately
      const tagWithName: MediaTag = {
        ...newTag,
        person_name: variables.personName,
        person_type: variables.personType,
      };
      setLocalTags((prev) => [...prev, tagWithName]);
      queryClient.invalidateQueries({ queryKey: ["mediaLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
      setCurrentRegion(null);
      setShowPersonSelect(false);
    },
  });

  // Delete tag mutation
  const deleteTagMutation = useMutation({
    mutationFn: async (tagId: number) => {
      const token = await getAccessTokenSilently();
      return deleteMediaTag(media.id, tagId, token);
    },
    onSuccess: (_data, tagId) => {
      // Update local tags immediately
      setLocalTags((prev) => prev.filter((t) => t.id !== tagId));
      queryClient.invalidateQueries({ queryKey: ["mediaLibrary"] });
      setSelectedTagId(null);
    },
  });

  // Handle image load
  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      setImageDimensions({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight,
      });
      setImageLoaded(true);
    }
  }, []);

  // Get pointer position relative to image as percentage
  const getRelativePosition = useCallback((e: React.PointerEvent): { x: number; y: number } | null => {
    if (!imageRef.current) return null;
    const rect = imageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
  }, []);

  // Handle pointer down - start drawing
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (selectedTagId) return; // Don't draw if a tag is selected
    // Prevent default to stop scrolling/zooming on touch devices
    e.preventDefault();
    const pos = getRelativePosition(e);
    if (pos) {
      setIsDrawing(true);
      setDrawStart(pos);
      setCurrentRegion(null);
    }
  }, [getRelativePosition, selectedTagId]);

  // Handle pointer move - update drawing
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDrawing || !drawStart) return;
    const pos = getRelativePosition(e);
    if (pos) {
      setCurrentRegion({
        x: Math.min(drawStart.x, pos.x),
        y: Math.min(drawStart.y, pos.y),
        width: Math.abs(pos.x - drawStart.x),
        height: Math.abs(pos.y - drawStart.y),
      });
    }
  }, [isDrawing, drawStart, getRelativePosition]);

  // Handle pointer up - finish drawing
  const handlePointerUp = useCallback(() => {
    if (isDrawing && currentRegion && currentRegion.width > 0.01 && currentRegion.height > 0.01) {
      setShowPersonSelect(true);
    }
    setIsDrawing(false);
    setDrawStart(null);
  }, [isDrawing, currentRegion]);

  // Handle clicking on existing tag
  const handleTagClick = useCallback((tag: MediaTag, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTagId(selectedTagId === tag.id ? null : tag.id);
    setCurrentRegion(null);
    setShowPersonSelect(false);
  }, [selectedTagId]);

  // Handle person selection for new tag (from PersonSelector)
  const handlePersonSelect = useCallback((
    personType: "tree" | "connected" | "name_only",
    personId: number,
    personName: string
  ) => {
    if (currentRegion) {
      createTagMutation.mutate({ personType, personId, region: currentRegion, personName });
    }
  }, [currentRegion, createTagMutation]);

  // Cancel current operation
  const handleCancel = useCallback(() => {
    setCurrentRegion(null);
    setShowPersonSelect(false);
    setSelectedTagId(null);
    setIsDrawing(false);
    setDrawStart(null);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div className="relative max-h-[90vh] max-w-[90vw] rounded-lg bg-white overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between border-b px-4 py-3">
          <div>
            <h2 className="font-semibold text-vault-900">Tag People in Photo</h2>
            <p className="text-xs text-vault-400">Tags are saved automatically</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-vault-500">
              {localTags.length} tagged
            </span>
            <button
              onClick={onClose}
              className="rounded-lg bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="flex-shrink-0 bg-vault-50 px-4 py-2 text-sm text-vault-600">
          Draw a box around a face to tag a person. Click existing tags to view or delete.
        </div>

        {/* Image Container */}
        <div
          ref={containerRef}
          className="relative cursor-crosshair overflow-hidden flex-1 min-h-0"
          style={{ maxHeight: "calc(90vh - 200px)", touchAction: "none" }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          {mediaLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-vault-300 border-t-vault-600" />
            </div>
          ) : mediaUrl ? (
            <img
              ref={imageRef}
              src={mediaUrl}
              alt={media.original_filename}
              className="max-h-[calc(90vh-120px)] w-auto"
              onLoad={handleImageLoad}
              draggable={false}
            />
          ) : (
            <div className="flex h-64 items-center justify-center text-vault-400">
              Failed to load image
            </div>
          )}

          {/* Existing Tags */}
          {imageLoaded && localTags.map((tag) => {
            const isConnected = tag.person_type === "connected" || tag.connected_person_id;
            const isNameOnly = tag.person_type === "name_only" || (tag.tag_name && !tag.person_id && !tag.connected_person_id);
            const displayName = tag.person_name || tag.tag_name || 
              (tag.person_id ? `Person #${tag.person_id}` : 
               tag.connected_person_id ? `Connected #${tag.connected_person_id}` : "Unknown");
            
            // Determine tag styling based on type
            const getBorderClass = () => {
              if (selectedTagId === tag.id) return "border-blue-500 bg-blue-500/20";
              if (isNameOnly) return "border-gray-400 bg-gray-400/10 hover:bg-gray-400/20";
              if (isConnected) return "border-amber-400 bg-amber-400/10 hover:bg-amber-400/20";
              return "border-yellow-400 bg-yellow-400/10 hover:bg-yellow-400/20";
            };
            
            const getLabelClass = () => {
              if (isNameOnly) return "bg-gray-600/90";
              if (isConnected) return "bg-amber-600/90";
              return "bg-black/75";
            };
            
            return (
              tag.region_x !== null && tag.region_y !== null && (
                <div
                  key={tag.id}
                  className={`absolute border-2 cursor-pointer transition-all ${getBorderClass()}`}
                  style={{
                    left: `${(tag.region_x || 0) * 100}%`,
                    top: `${(tag.region_y || 0) * 100}%`,
                    width: `${(tag.region_width || 0) * 100}%`,
                    height: `${(tag.region_height || 0) * 100}%`,
                  }}
                  onClick={(e) => handleTagClick(tag, e)}
                >
                  <div className={`absolute -bottom-6 left-0 whitespace-nowrap rounded px-1 text-xs text-white ${getLabelClass()}`}>
                    {isNameOnly && "📝 "}
                    {isConnected && !isNameOnly && "⚭ "}
                    {displayName}
                  </div>
                </div>
              )
            );
          })}

          {/* Current Drawing Region */}
          {currentRegion && (
            <div
              className="absolute border-2 border-dashed border-green-500 bg-green-500/20"
              style={{
                left: `${currentRegion.x * 100}%`,
                top: `${currentRegion.y * 100}%`,
                width: `${currentRegion.width * 100}%`,
                height: `${currentRegion.height * 100}%`,
              }}
            />
          )}
        </div>

        {/* Person Selection Popup */}
        {showPersonSelect && currentRegion && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
            <PersonSelector
              treePersons={persons}
              onSelect={handlePersonSelect}
              onCancel={handleCancel}
              allowCreateConnected={true}
              placeholder="Search family members or connected people..."
            />
          </div>
        )}

        {/* Selected Tag Actions */}
        {selectedTagId && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-lg bg-white shadow-xl border p-4">
            <p className="text-sm text-vault-700 mb-2">
              {localTags.find(t => t.id === selectedTagId)?.person_name || "Tagged person"}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => deleteTagMutation.mutate(selectedTagId)}
                className="rounded bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
              >
                Delete Tag
              </button>
              <button
                onClick={() => setSelectedTagId(null)}
                className="rounded bg-vault-100 px-4 py-2 text-sm text-vault-600 hover:bg-vault-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Footer with tag list */}
        <div className="flex-shrink-0 border-t px-4 py-4 bg-vault-50 min-h-[60px]">
          <div className="flex flex-wrap gap-2 items-center">
            {localTags.length === 0 ? (
              <span className="text-sm text-vault-500">No tags yet. Draw a box to add one.</span>
            ) : (
              localTags.map((tag) => {
                const isConnected = tag.person_type === "connected" || tag.connected_person_id;
                const isNameOnly = tag.person_type === "name_only" || (tag.tag_name && !tag.person_id && !tag.connected_person_id);
                const displayName = tag.person_name || tag.tag_name || 
                  (tag.person_id ? `Person #${tag.person_id}` : 
                   tag.connected_person_id ? `Connected #${tag.connected_person_id}` : "Unknown");
                
                const getTagClass = () => {
                  if (selectedTagId === tag.id) return "bg-blue-100 text-blue-700";
                  if (isNameOnly) return "bg-gray-100 text-gray-700 hover:bg-gray-200";
                  if (isConnected) return "bg-amber-100 text-amber-700 hover:bg-amber-200";
                  return "bg-vault-200 text-vault-700 hover:bg-vault-300";
                };
                
                return (
                  <span
                    key={tag.id}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm cursor-pointer ${getTagClass()}`}
                    onClick={() => setSelectedTagId(selectedTagId === tag.id ? null : tag.id)}
                  >
                    {isNameOnly && <span className="text-xs">📝</span>}
                    {isConnected && !isNameOnly && <span className="text-xs">⚭</span>}
                    {displayName}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTagMutation.mutate(tag.id);
                      }}
                      className="ml-1 text-vault-500 hover:text-red-600"
                    >
                      ×
                    </button>
                  </span>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
